import { Button } from '@heroui/react';
import { MoveHorizontal, MoveVertical } from 'lucide-react';
import { MaterialUnit, normalizeMaterialUnit, type MaterialLibrary } from '@estimator/shared';

interface BoardRunControlProps {
  /** The panel's two dimensions in inches, in whatever order the row holds them. */
  widthIn: number;
  lengthIn: number;
  /** The material selected on this row, if any. */
  material: MaterialLibrary | null | undefined;
  flipped: boolean;
  onChange: (flipped: boolean) => void;
}

/**
 * Flips which of a panel's dimensions the boards run along.
 *
 * Only shown for linear-foot materials, and only meaningful because rips round
 * up to whole boards: with exact division both orientations cost the same, so
 * there would be nothing to choose. A 42x50 panel off a 5" board is
 * ceil(42/5)=9 boards x 50" = 450" the default way and ceil(50/5)=10 boards x
 * 42" = 420" flipped — so the longer side is not automatically the cheaper
 * run, which is why this is a control rather than an optimisation.
 *
 * Deliberately manual: run direction is usually a grain decision, so picking
 * the cheaper orientation automatically would silently cut boards a way the
 * shop would not.
 */
export default function BoardRunControl({
  widthIn, lengthIn, material, flipped, onChange,
}: BoardRunControlProps) {
  // Nothing to choose unless the boards are bought by the linear foot.
  if (normalizeMaterialUnit(material?.unit) !== MaterialUnit.LINEAR_FOOT) return null;

  const longer = Math.max(widthIn, lengthIn);
  const shorter = Math.min(widthIn, lengthIn);
  const run = flipped ? shorter : longer;
  const cover = flipped ? longer : shorter;

  const boardWidth = material?.sheet_width ?? 0;
  const boards = boardWidth > 0 ? Math.ceil(cover / boardWidth) : null;

  const title = boards
    ? `Boards run along ${run}" — ${boards} x ${run}" = ${(boards * run / 12).toFixed(1)} lf. Click to flip.`
    : `Boards run along ${run}". Set a Board Width on this material to price it. Click to flip.`;

  return (
    <Button
      isIconOnly
      size="sm"
      variant="light"
      aria-label={title}
      title={title}
      onPress={() => onChange(!flipped)}
    >
      {flipped
        ? <MoveVertical size={14} style={{ color: 'var(--accent)' }} />
        : <MoveHorizontal size={14} style={{ color: 'var(--text-muted)' }} />}
    </Button>
  );
}
